var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import "../types.js";
import { registerSystem, registerPixiComponent, DEPTHS } from "../ComponentRegistry.js";
import { F as FaceDirectionEnum, C as ComponentCategory } from "../../enums-86f58524.js";
import * as PIXI from "pixi.js";
import { PixiSpineLoader } from "../../loader/PixiSpineLoader.js";
import { A as AttachSchema } from "../../Attach-abbbd93e.js";
import { L as LocomotionSchema } from "../../Locomotion-379972de.js";
import { R as RadiusSchema } from "../../Radius-89a665f8.js";
import { T as TransformSchema } from "../../Transform-9450b7b5.js";
import { S as SpineSchema } from "../../Spine-11fceea9.js";
import "../../decorators/type.js";
import "ajv";
import "../../utils/clone.js";
import "bitecs";
import "../../utils/typehelpers.js";
import "../../global-33d5eebf.js";
import "../../utils/byteArray.js";
import "pixi-spine";
import "../../utils/vector.js";
class SpineSystem {
  constructor() {
    __publicField(this, "type", "Spine");
    __publicField(this, "category", ComponentCategory.RENDERING);
    __publicField(this, "schema", SpineSchema);
    __publicField(this, "depth", DEPTHS.DRAW + 1);
  }
  run(entity, gameModel) {
    gameModel.getTypedUnsafe(entity, SpineSchema);
  }
}
registerSystem(SpineSystem);
class SpineComponentPixi {
  constructor() {
    __publicField(this, "ids", /* @__PURE__ */ new Set());
    __publicField(this, "schema", SpineSchema);
    __publicField(this, "instances", {});
    __publicField(this, "animationCache", {});
    __publicField(this, "imageCache", {});
  }
  transform(pixiData, entity, data, gameModel, viewport) {
    const { spine, container } = pixiData;
    const locomotionSchema = gameModel.getTypedUnsafe(entity, LocomotionSchema);
    const direction = { x: locomotionSchema.directionX, y: locomotionSchema.directionY };
    if (data.faceDirection === FaceDirectionEnum.HORIZONTAL) {
      let xDirection = direction.x;
      if (gameModel.hasComponent(entity, AttachSchema)) {
        const owner = gameModel.getComponentUnsafe(entity, AttachSchema).parent;
        xDirection = LocomotionSchema.store.directionX[owner];
      }
      if (!data.antiJitterTime || gameModel.timeElapsed - pixiData.lastFlip > data.antiJitterTime) {
        if (xDirection < 0) {
          spine.scale.x = -1;
        } else {
          spine.scale.x = 1;
        }
        pixiData.lastFlip = gameModel.timeElapsed;
      }
    }
    if (data.xscale !== void 0) {
      spine.scale.x *= data.xscale;
    }
    if (data.yscale !== void 0) {
      spine.scale.y *= data.yscale;
    } else if (data.faceDirection === FaceDirectionEnum.VERTICAL) {
      if (!data.antiJitterTime || gameModel.timeElapsed - pixiData.lastFlip > data.antiJitterTime) {
        container.scale.y = direction.y < 0 ? -1 * data.scale : 1 * data.scale;
        pixiData.lastFlip = gameModel.timeElapsed;
      }
    }
    if (container.scale.x !== data.scale) {
      container.scale.set(data.scale);
    }
    const verticalDistanceFromCenter = Math.abs(pixiData.container.y - viewport.center.y);
    const horizontalDistanceFromCenter = Math.abs(pixiData.container.x - viewport.center.x);
    if (horizontalDistanceFromCenter - pixiData.container.width / 2 > 1920 || verticalDistanceFromCenter - pixiData.container.height / 2 > 1080) {
      container.visible = false;
    } else {
      container.visible = true;
    }
  }
  init(entity, gameModel, viewport) {
    var _a, _b, _c;
    const spineData = gameModel.getTypedUnsafe(entity, this.schema);
    let zIndex = 2;
    const instance = {
      container: ((_a = this.instances[entity]) == null ? void 0 : _a.container) ?? new PIXI.Container(),
      debug: (_b = this.instances[entity]) == null ? void 0 : _b.debug,
      lastFlip: 0
    };
    if (!instance.debug) {
      instance.debug = new PIXI.Container();
      instance.debug.visible = false;
      viewport.addChild(instance.debug);
    }
    const spineInstance = PixiSpineLoader.getInstance().get(spineData.spineKey);
    if (spineData.skin) {
      spineInstance.skeleton.setSkinByName(spineData.skin);
    }
    spineInstance.state.setAnimation(0, spineData.currentAnimation, true);
    spineInstance.state.timeScale = spineData.animationSpeed || 1;
    spineInstance.autoUpdate = true;
    zIndex = spineData.zIndex || 2;
    instance.container.addChild(spineInstance);
    instance.spine = spineInstance;
    instance.spineKey = spineData.currentAnimation;
    instance.container.zIndex = zIndex;
    instance.spine.position.set(0, 0);
    (_c = instance.container) == null ? void 0 : _c.scale.set(spineData.scale);
    this.instances[entity] = instance;
    viewport.addChild(instance.container);
    this.ids.add(entity);
  }
  run(entity, gameModel, viewport) {
    const spineData = gameModel.getTypedUnsafe(entity, this.schema);
    if (this.instances[entity] && (spineData.currentAnimation ?? spineData.spineKey) !== this.instances[entity].spineKey) {
      this.cleanup(entity, gameModel, viewport);
    }
    if (!this.instances[entity]) {
      this.init(entity, gameModel, viewport);
    }
    const pixiData = this.instances[entity];
    const { spine, container, debug } = pixiData;
    if (spineData.opacity === 0) {
      spine.visible = false;
    } else {
      spine.visible = true;
      spine.alpha = 1;
    }
    const transformSchema = gameModel.getTypedUnsafe(entity, TransformSchema);
    const position = transformSchema.position;
    position.y -= transformSchema.z;
    let xoffset = 0;
    let yoffset = 0;
    const viewY = viewport.position.y;
    if (gameModel.hasComponent(entity, AttachSchema)) {
      const owner = gameModel.getComponentUnsafe(entity, AttachSchema).parent;
      container.zIndex = TransformSchema.store.y[owner] - viewY + TransformSchema.store.z[owner] + RadiusSchema.store.radius[owner] + spineData.zIndex;
    } else {
      container.zIndex = TransformSchema.store.y[entity] - viewY + TransformSchema.store.z[entity] + RadiusSchema.store.radius[entity] + spineData.zIndex;
    }
    container.x = position.x + xoffset;
    container.y = position.y + yoffset;
    debug == null ? void 0 : debug.position.set(position.x, position.y);
    this.transform(pixiData, entity, spineData, gameModel, viewport);
  }
  cleanup(entity, gameModel, viewport) {
    const instanceData = this.instances[entity];
    if (!instanceData) {
      return;
    }
    instanceData.container.destroy();
    const instance = this.instances[entity].spine;
    delete this.instances[entity];
    instance.visible = false;
    const spineData = gameModel.getTypedUnsafe(entity, this.schema);
    if (spineData.spineKey) {
      const key = instanceData.spineKey;
      this.animationCache[key] = this.animationCache[key] ?? [];
      this.animationCache[key].push(instance);
    } else {
      this.imageCache[instanceData.spineKey] = this.imageCache[instanceData.spineKey] ?? [];
      this.imageCache[instanceData.spineKey].push(instance);
    }
    this.ids.delete(entity);
  }
}
registerPixiComponent("Spine", SpineComponentPixi);
export {
  SpineComponentPixi
};
//# sourceMappingURL=Spine.js.map
